var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import { useCallback, useRef, useState } from 'react';
import { useDataProvider, useGetManyReference, useNotify, useRecordContext, useRegisterMutationMiddleware, useResourceContext, useSaveContext, } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { ReferenceOneInputPrefix } from './constants';
export var useReferenceOneInputController = function (options) {
    var reference = options.reference, target = options.target, _a = options.source, source = _a === void 0 ? 'id' : _a, defaultValue = options.defaultValue, _b = options.sort, sort = _b === void 0 ? { field: 'id', order: 'ASC' } : _b, _c = options.filter, filter = _c === void 0 ? {} : _c, _d = options.queryOptions, queryOptions = _d === void 0 ? {} : _d;
    var saveContext = useSaveContext();
    if (saveContext.mutationMode === 'undoable') {
        throw new Error('ReferenceOneInputController cannot be used with undoable mutations. Set mutationMode="optimistic" or mutationMode="pessimistic" in the parent <Edit> or <Create>.');
    }
    var dataProvider = useDataProvider();
    var notify = useNotify();
    var resource = useResourceContext(options);
    var record = useRecordContext(options);
    /**
     * As the edited record does not contains the referenced record values,
     * we have to store them in a temporary field in the current form.
     * This field will be removed from the form values before the final submit.
     */
    var temporaryFieldName = getReferenceOneFormField({
        resource: resource,
        reference: reference,
        target: target,
    });
    var form = useFormContext();
    /**
     * Ref to hold the related record fetched from the dataProvider
     */
    var relatedRecord = useRef(null);
    var meta = queryOptions.meta, otherQueryOptions = __rest(queryOptions, ["meta"]);
    var _e = useState(false), isSettled = _e[0], setIsSettled = _e[1];
    var enabled = typeof record === 'object' && record.hasOwnProperty(source);
    var _f = useGetManyReference(reference, {
        target: target,
        id: get(record, source),
        pagination: { page: 1, perPage: 1 },
        sort: sort,
        filter: filter,
        meta: meta,
    }, __assign(__assign({ enabled: enabled, refetchOnReconnect: false, refetchOnWindowFocus: false }, otherQueryOptions), { onSuccess: function (res) {
            // filter out not found references - happens when the dataProvider doesn't guarantee referential integrity
            var finalData = (res.data ? res.data.filter(Boolean) : [])[0];
            relatedRecord.current = finalData;
            if (!finalData && defaultValue) {
                form.setValue(temporaryFieldName, defaultValue, {
                    shouldDirty: true,
                });
            }
            else {
                form.setValue(temporaryFieldName, finalData, {
                    shouldDirty: false,
                    shouldValidate: false,
                    shouldTouch: false,
                });
            }
            setIsSettled(true);
            if (queryOptions.onSuccess) {
                queryOptions.onSuccess(res);
            }
        }, onError: function (error) {
            setIsSettled(true);
            if (queryOptions.onError) {
                queryOptions.onError(error);
            }
        } })), error = _f.error, isLoading = _f.isLoading, isFetching = _f.isFetching, refetch = _f.refetch;
    /**
     * Handle the submit of the form
     */
    var middleware = useCallback(function (resource, params, options, next) { return __awaiter(void 0, void 0, void 0, function () {
        var data, otherParams, onSuccess, otherOptions, newRelatedRecord, sanitizedData, handleSuccess;
        return __generator(this, function (_a) {
            data = params.data, otherParams = __rest(params, ["data"]);
            onSuccess = options.onSuccess, otherOptions = __rest(options, ["onSuccess"]);
            newRelatedRecord = get(data, temporaryFieldName);
            sanitizedData = omit(data, temporaryFieldName.split('.')[0]);
            handleSuccess = function (data) { return __awaiter(void 0, void 0, void 0, function () {
                var oldRelatedRecord, shouldCreate, shouldUpdate, query, error_1;
                var _a;
                return __generator(this, function (_b) {
                    switch (_b.label) {
                        case 0:
                            oldRelatedRecord = relatedRecord.current;
                            shouldCreate = !oldRelatedRecord &&
                                Object.values(newRelatedRecord).some(Boolean);
                            shouldUpdate = oldRelatedRecord &&
                                Object.values(newRelatedRecord).some(Boolean) &&
                                !isEqual(oldRelatedRecord, newRelatedRecord);
                            query = null;
                            if (shouldCreate) {
                                query = dataProvider.create(reference, {
                                    data: __assign(__assign({}, newRelatedRecord), (_a = {}, _a[target] = get(data, source), _a)),
                                });
                            }
                            else if (shouldUpdate) {
                                query = dataProvider.update(reference, {
                                    id: newRelatedRecord.id,
                                    data: newRelatedRecord,
                                    previousData: oldRelatedRecord,
                                });
                            }
                            _b.label = 1;
                        case 1:
                            _b.trys.push([1, 3, , 4]);
                            return [4 /*yield*/, query];
                        case 2:
                            _b.sent();
                            return [3 /*break*/, 4];
                        case 3:
                            error_1 = _b.sent();
                            notify('ra-relationships.referenceOneInput.saveError', {
                                type: 'error',
                                messageArgs: {
                                    _: 'Server error: your changes were not completely saved',
                                },
                            });
                            // do not execute success side effects
                            return [2 /*return*/];
                        case 4:
                            if (sanitizedData.hasOwnProperty(source)) {
                                // if we're in an edit page (not a create page)
                                // force refresh (and update of references.current) to allow further saves
                                refetch();
                            }
                            if (onSuccess) {
                                onSuccess(data);
                            }
                            return [2 /*return*/];
                    }
                });
            }); };
            next(resource, __assign(__assign({}, otherParams), { data: sanitizedData }), __assign(__assign({}, otherOptions), { onSuccess: handleSuccess }));
            return [2 /*return*/];
        });
    }); }, [
        dataProvider,
        notify,
        reference,
        refetch,
        source,
        target,
        temporaryFieldName,
    ]);
    useRegisterMutationMiddleware(middleware);
    return {
        error: error,
        isLoading: enabled && (isLoading || !isSettled),
        isFetching: isFetching,
        refetch: refetch,
        temporaryFieldName: temporaryFieldName,
    };
};
/**
 * Get virtual field name
 *
 * @example
 * getReferenceOneFormField({ resource: 'books', reference: 'book_details', target: 'books_id' })
 * // '@@ra-one/books/book_details/books_id.0'
 */
export var getReferenceOneFormField = function (_a) {
    var resource = _a.resource, reference = _a.reference, target = _a.target;
    // Adding '.0' at the end helps the getFieldLabelTranslationArgs helper to compute the default label
    return "".concat(ReferenceOneInputPrefix).concat(resource, "/").concat(reference, "/").concat(target, ".0");
};
